import React, {
    useState,
    ReactNode,
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
    useCallback,
    useEffect,
} from "react"
import { useLocalStorage } from "../components/hooks"
import { Product } from "../components/Product/productTypes"
import { DbEntry } from "../components/types"
import { Discount, ProductDiscount } from "../components/Discount/discountTypes"
import { calculatePriceForProduct } from "../components/Cart/cartUtils"
import { CartOrder } from "../components/Cart/cartTypes"
import { roundTo2Decimal } from "../components/utils/utils"

const ONE_HOUR = 60 * 60 * 1000
const TWO_WEEKS = 14 * 24 * ONE_HOUR

const CartStateContext = createContext<CartOrder | undefined>(undefined)
const CartUpdaterContext = createContext<
    Dispatch<SetStateAction<CartOrder>> | undefined
>(undefined)

type Props = {
    children: ReactNode
}

function CartProvider(props: Props) {
    const [storedValue, setStoredValue] = useLocalStorage<CartOrder>("order", {
        cart: {},
        buyerId: null,
    })
    const [orderCartCreated, setOrderCartCreated] = useLocalStorage<number>(
        "orderCreated",
        Date.now()
    )
    const [order, setOrder] = useState<CartOrder>(storedValue)

    useEffect(() => {
        if (Date.now() - orderCartCreated > TWO_WEEKS) {
            setOrder({ cart: {}, buyerId: null })
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        setStoredValue(order)
        setOrderCartCreated(Date.now())
        // eslint-disable-next-line
    }, [order])

    return (
        <CartStateContext.Provider value={order}>
            <CartUpdaterContext.Provider value={setOrder}>
                {props.children}
            </CartUpdaterContext.Provider>
        </CartStateContext.Provider>
    )
}

function useCartState() {
    const orderState = useContext(CartStateContext)
    if (orderState === undefined) {
        throw new Error("useOrderState must be used within an OrderProvider")
    }

    return orderState
}

function useCartUpdater() {
    const setOrder = useContext(CartUpdaterContext)

    if (setOrder === undefined) {
        throw new Error("useOrderUpdater must be used within an OrderProvider")
    }

    const addToCart = useCallback(
        (product: DbEntry<Product>, discount?: Discount) =>
            setOrder((o) => {
                const increaseValue = product.data?.orderUnitAmount
                    ? Number(product.data?.orderUnitAmount)
                    : 1

                const newNumberInOrder = o.cart[product.id]
                    ? o.cart[product.id].numberInOrder + increaseValue
                    : increaseValue

                let calculatedPrice = roundTo2Decimal(
                    Number(product.data?.basePrice) * newNumberInOrder
                )

                let perPiecePrice = Number(product.data?.basePrice)

                if (discount) {
                    const { sumPrice, perPiecePrice: perPiece } =
                        calculatePriceForProduct(
                            Number(product.data?.basePrice),
                            discount,
                            newNumberInOrder,
                            Number(product.data?.orderUnitAmount)
                        )
                    calculatedPrice = sumPrice
                    perPiecePrice = perPiece
                }

                return {
                    ...o,
                    cart: {
                        ...o.cart,
                        [product.id]: o && {
                            productInfo: product.data,
                            numberInOrder: newNumberInOrder,
                            calculatedPrice,
                            perPiecePrice,
                        },
                    },
                }
            }),
        [setOrder]
    )

    const deductFromCart = useCallback(
        (product: DbEntry<Product>, discount?: Discount) =>
            setOrder((o) => {
                const decreaseValue = product.data?.orderUnitAmount
                    ? Number(product.data?.orderUnitAmount)
                    : 1
                const newNumberInOrder =
                    o.cart[product.id] &&
                    o.cart[product.id].numberInOrder &&
                    o.cart[product.id].numberInOrder - decreaseValue

                if (newNumberInOrder === 0) {
                    const { [product.id]: productToRemove, ...restProducts } =
                        o.cart
                    return {
                        ...o,
                        cart: restProducts,
                    }
                } else {
                    let calculatedPrice = roundTo2Decimal(
                        Number(product.data?.basePrice) * newNumberInOrder
                    )
                    let perPiecePrice = Number(product.data?.basePrice)

                    if (discount) {
                        const { sumPrice, perPiecePrice: perPiece } =
                            calculatePriceForProduct(
                                Number(product.data?.basePrice),
                                discount,
                                newNumberInOrder,
                                Number(product.data?.orderUnitAmount)
                            )
                        calculatedPrice = sumPrice
                        perPiecePrice = perPiece
                    }
                    return {
                        ...o,
                        cart: {
                            ...o.cart,
                            [product.id]: o && {
                                productInfo: product.data,
                                numberInOrder: newNumberInOrder,
                                calculatedPrice,
                                perPiecePrice,
                            },
                        },
                    }
                }
            }),
        [setOrder]
    )

    const removeProductFromCart = useCallback(
        (productId: string) => {
            setOrder((o) => {
                const { [productId]: orderToRemove, ...restOrder } = o.cart
                return {
                    ...o,
                    cart: restOrder,
                }
            })
        },
        [setOrder]
    )

    const updateCartDiscount = useCallback(
        (discounts: ProductDiscount) => {
            setOrder((o) => ({
                ...o,
                cart: Object.keys(o.cart).reduce((prevValue, productId) => {
                    const orderDetails = o.cart[productId]
                    const { sumPrice: calculatedPrice, perPiecePrice } =
                        calculatePriceForProduct(
                            Number(orderDetails.productInfo?.basePrice),
                            discounts[productId],
                            orderDetails.numberInOrder,
                            Number(orderDetails.productInfo?.orderUnitAmount)
                        )
                    return {
                        ...prevValue,
                        [productId]: {
                            ...orderDetails,
                            calculatedPrice,
                            perPiecePrice,
                        },
                    }
                }, {}),
            }))
        },
        [setOrder]
    )

    const setBuyer = useCallback(
        (buyerId: string | null) => {
            setOrder((o) => ({
                ...o,
                buyerId,
            }))
        },
        [setOrder]
    )

    const clearCart = useCallback(
        () => setOrder({ cart: {}, buyerId: null }),
        [setOrder]
    )

    return {
        addToCart,
        deductFromCart,
        clearCart,
        removeProductFromCart,
        updateCartDiscount,
        setBuyer,
    }
}

export { CartProvider, useCartState, useCartUpdater }
